/**
 * Playground state machine — the "try combinations" sandbox in the tutorial
 * popup. Pure: no React, no DOM. The tour engine drives it through
 * `step.playground` patches, the UI through the same actions.
 *
 * Model
 *   network : good | slow | unstable | offline
 *   paused  : null | "user" | "auto"
 *   reloaded: bool          (page reload / closed tab → files must be re-picked)
 *   device  : this | other  (upload started on another device)
 *   phase   : uploading | finalizing | done | canceled
 *
 * deriveDemoKey() maps a model to one of the DemoUploadCard fixtures so the
 * card always shows exactly what the live card would show.
 */

export const NETWORKS = ["good", "slow", "unstable", "offline"];

export const NETWORK_LABEL = {
  good: "Good",
  slow: "Slow",
  unstable: "Unstable",
  offline: "Offline",
};

export const initialPlaygroundState = () => ({
  network: "good",
  paused: null,
  reloaded: false,
  device: "this",
  phase: "uploading",
});

export const playgroundReducer = (state, action) => {
  switch (action.type) {
    case "reset":
      return initialPlaygroundState();
    case "patch":
      return { ...state, ...action.patch };
    case "network":
      // Coming back online after an auto-pause resumes by itself (AutoResumeWatcher).
      if (action.network !== "offline" && state.paused === "auto") {
        return { ...state, network: action.network, paused: null };
      }
      return { ...state, network: action.network };
    case "pause":
      if (state.phase !== "uploading" || state.device !== "this") return state;
      return { ...state, paused: "user" };
    case "resume":
      if (state.reloaded) return state; // must re-pick first
      return { ...state, paused: null };
    case "longOutage":
      // Offline long enough → the app auto-pauses to protect the upload.
      if (state.network !== "offline" || state.paused) return state;
      return { ...state, paused: "auto" };
    case "reload":
      if (state.phase !== "uploading" || state.device !== "this") return state;
      return { ...state, reloaded: true, paused: state.paused || "user" };
    case "repick":
      if (!state.reloaded) return state;
      return { ...state, reloaded: false, paused: null };
    case "otherDevice":
      return { ...state, device: "other", paused: state.paused || "user", reloaded: false };
    case "takeOver":
      // Real flow (packagesSlice.claimOne): the claimed package is rehydrated as
      // a paused card without file handles → the user re-selects the files.
      return { ...state, device: "this", paused: "user", reloaded: true };
    case "finish":
      if (state.paused || state.reloaded || state.device !== "this") return state;
      if (state.phase === "uploading") return { ...state, phase: "finalizing" };
      if (state.phase === "finalizing") return { ...state, phase: "done" };
      return state;
    case "cancel":
      return { ...state, phase: "canceled" };
    default:
      return state;
  }
};

export const deriveDemoKey = (s) => {
  if (s.phase === "canceled") return "canceled";
  if (s.device === "other") return "anotherDevice";
  if (s.phase === "done") return "completed";
  if (s.phase === "finalizing") return "finalizing";
  if (s.reloaded) return "interrupted";
  if (s.paused === "user") return "pausedByUser";
  if (s.paused === "auto") return "autoPaused";
  switch (s.network) {
    case "slow":
      return "slow";
    case "unstable":
      return "stalled";
    case "offline":
      return "retrying";
    default:
      return "uploading";
  }
};

// Which event buttons make sense right now — the UI disables the rest so a
// contributor isn't shown combinations the real app can never produce.
export const availableEvents = (s) => ({
  reload: s.phase === "uploading" && s.device === "this" && !s.reloaded,
  otherDevice: s.device === "this" && s.phase !== "canceled" && s.phase !== "done",
  longOutage: s.network === "offline" && !s.paused && s.phase === "uploading" && s.device === "this",
  finish: s.phase !== "done" && s.phase !== "canceled" && !s.paused && !s.reloaded && s.device === "this",
  reset: true,
});
